import { ToastrService } from 'ngx-toastr';
import pluralize from 'pluralize';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';

import { ISnListResponse, ISnFeedQueryParameters, ISnListQueryParameters } from '@lib/api';
import { SnUser, SnTeam, SnShare, UserMfaDevice, MfaBackup } from '@lib/api/entities';
import { SnLiveUser } from '@lib/live-tracking/models';
import { ISnCanShare } from '@lib/share';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnUsersAPI implements ISnCanShare {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  private _getUserLabel(count: number): string {
    return count === 1 ? t('users.labels.single') : t('users.labels.plural');
  }

  public unlockProfile(userId: any): Observable<SnUser> {
    return this._httpClient.post(`/api/users/${userId}/unlock/`, undefined).pipe(
      map((response: any) => new SnUser(response)),
      tap({
        next: () => this._toastrService.success(t('users.toasts.userUnlocked')),
        error: () => this._toastrService.error(t('users.toasts.userUnlockFailed')),
      }),
    );
  }

  public getUserList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    return this._httpClient.get('/api/users/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((user) => new SnUser(user)),
      })),
    );
  }

  public getRequestUserList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    return this._httpClient.get('/api/users/requested-users/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((user) => new SnUser(user)),
      })),
    );
  }

  public activateUser(id: string, data: any): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('users.areYouSureActivateUser'),
        confirmButton: {
          text: 'common.confirm',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.post(`/api/users/requested-users/${id}/activate/`, data)),
        tap({
          next: () => this._toastrService.success(t('users.toasts.userActivated')),
          error: () =>
            this._toastrService.error(t('users.toasts.userActivateFailed')),
        }),
      );
  }

  public activate(id: string, data: any): Observable<Object> {
    return this._httpClient.post(`/api/users/requested-users/${id}/activate/`, data).pipe(
      tap({
        next: () => this._toastrService.success(t('users.toasts.userActivated')),
        error: () => this._toastrService.error(t('users.toasts.userActivateFailed')),
      }),
    );
  }

  public feedUsers(params: ISnFeedQueryParameters): Observable<SnUser[]> {
    return this._httpClient
      .get('/api/users/feed/', { params })
      .pipe(map((response: any) => response.map((user) => new SnUser(user))));
  }

  public feedUsersRequested(params: ISnFeedQueryParameters): Observable<SnUser[]> {
    return this._httpClient
      .get('/api/users/requested-users/feed/', { params })
      .pipe(map((response: any) => response.map((user) => new SnUser(user))));
  }

  public getRawUser(id: string): Observable<Object> {
    return this._httpClient.get(`/api/users/${id}/`);
  }

  public getUser(id: string): Observable<SnUser> {
    return this.getRawUser(id).pipe(map((response: any) => new SnUser(response)));
  }

  public createUser(data: any): Observable<SnUser> {
    return this._httpClient.post('/api/auth/user/create/', data).pipe(
      map((response: any) => new SnUser(response)),
      tap({
        next: () => this._toastrService.success('User created'),
        error: (e: HttpErrorResponse) => {
          Object.entries(e.error).forEach((results: any) => {
            this._toastrService.error(results[1]);
          });
        },
      }),
      catchError((error): Observable<any> => {
        if (error.status === 404) {
          return of('404 Error. The page cannot be found');
        }
        return throwError(error);
      }),
    );
  }

  public updateUser(
    id: string,
    data: any,
    showMessage: boolean = true,
    message: string = t('users.toasts.userUpdated'),
  ): Observable<SnUser> {
    return this._httpClient.patch(`/api/users/${id}/`, data).pipe(
      map((response: any) => new SnUser(response)),
      tap({
        next: () => (showMessage ? this._toastrService.success(message) : null),
        error: () => this._toastrService.error(t('users.toasts.userUpdateFailed')),
      }),
    );
  }

  public deactivateUser(
    id: string,
    email: string,
    data: any,
    showMessage: boolean = true,
  ): Observable<SnUser> {
    return this._dialogService
      .confirm({
        message: t('users.areYouSureDeactivateUser', { email }),
        confirmButton: {
          text: 'common.deactivate',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.patch(`/api/users/${id}/`, data)),
        map((response: any) => new SnUser(response)),
        tap({
          next: () =>
            showMessage ? this._toastrService.success(t('users.toasts.userDeactivated')) : null,
          error: () => this._toastrService.error(t('common.genericError')),
        }),
      );
  }

  public reactivateUser(
    id: string,
    email: string,
    data: any,
    showMessage: boolean = true,
  ): Observable<SnUser> {
    return this._dialogService
      .confirm({
        message: t('users.areYouSureReactivateUser', { email }),
        confirmButton: {
          text: 'users.actionsReactivate',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.patch(`/api/users/${id}/`, data)),
        map((response: any) => new SnUser(response)),
        tap({
          next: () =>
            showMessage ? this._toastrService.success(t('users.toasts.userReactivated')) : null,
          error: () => this._toastrService.error(t('common.genericError')),
        }),
      );
  }

  public deleteUser(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'users.areYouSureDeleteUser',
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
        alternativeButton: {
          text: 'users.deactivateUser',
          color: 'accent',
          value: 'deactivate',
        },
        typeDeleteToConfirm: true,
      })
      .pipe(
        switchMap((res) => {
          return res === true
            ? this._httpClient.delete(`/api/users/${id}/`).pipe(
                tap({
                  next: () => this._toastrService.success(t('users.toasts.userDeleted')),
                  error: () =>
                    this._toastrService.error(t('users.toasts.userDeleteFailed')),
                }),
              )
            : this._httpClient
                .patch(`/api/users/${id}/`, {
                  id,
                  isActive: false,
                })
                .pipe(
                  tap({
                    next: () => this._toastrService.success(t('users.toasts.userDeactivated')),
                    error: () =>
                      this._toastrService.error(t('users.toasts.userDeactivateFailed')),
                  }),
                );
        }),
      );
  }

  public deleteUserRequest(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'users.areYouSureDeleteUserRequest',
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/users/requested-users/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('users.toasts.userDeleted')),
          error: () => this._toastrService.error(t('users.toasts.userDeleteFailed')),
        }),
      );
  }

  public bulkDeleteUsers(ids: string[]): Observable<Object> {
    const count = ids.length;

    return this._dialogService
      .confirm({
        message: t('users.bulkDeleteUsers', { 
          count: count.toString(), 
          type: 'user' 
        }),
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
        typeDeleteToConfirm: true,
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.post('/api/users/bulk/delete/', { objects: ids });
        }),
        tap({
          next: () =>
            this._toastrService.success(
              t('users.toasts.usersDeleted', {
                count,
                label: this._getUserLabel(count),
              }),
            ),
          error: () => this._toastrService.error(t('users.toasts.usersDeleteFailed')),
        }),
      );
  }

  public importUsers(file: File): Observable<{ created: any; updated: any }> {
    const formData = new FormData();
    formData.append('file', file);

    return this._httpClient.post('/api/users/import/from-csv/', formData).pipe(
      tap({
          error: () => this._toastrService.error(t('users.toasts.usersImportFailed')),
      }),
      switchMap((response: any) => {
        const { created, updated, errors } = response;

        if (created) {
            this._toastrService.success(
              t('users.toasts.usersCreated', {
                count: created,
                label: this._getUserLabel(created),
              }),
            );
        }

        if (updated) {
            this._toastrService.warning(
              t('users.toasts.usersUpdated', {
                count: updated,
                label: this._getUserLabel(updated),
              }),
            );
        }

        if (errors) {
          return throwError(errors);
        }

        return of({ created, updated });
      }),
    );
  }

  public changeCallSign(
    id: string,
    callSign: string,
  ): Observable<{ team: SnTeam; callSign: string }> {
    return this._httpClient.post(`/api/users/${id}/change-call-sign/`, { callSign }).pipe(
      map((response: any) => ({
        team: new SnTeam(response.team),
        callSign,
      })),
      tap({
        next: () => this._toastrService.success(t('users.toasts.callSignChanged')),
        error: () => this._toastrService.error(t('users.toasts.callSignChangeFailed')),
      }),
    );
  }

  public validateUniqueEmail(exceptionEmail?: string): AsyncValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value || control.invalid) {
        return of(null);
      }

      if (exceptionEmail && control.value === exceptionEmail) {
        return of(null);
      }

      return this.getUserList({ email: control.value }).pipe(
        map(({ count }) => (count ? { unique: true } : null)),
      );
    };
  }

  public getShareList(
    userId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/users/${userId}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(userId: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/users/${userId}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(userId: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/users/${userId}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(userId: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'Are you sure you want to delete the share?',
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/users/${userId}/shares/${shareId}/`);
        }),
      );
  }

  public getMfaDeviceList(
    userId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<UserMfaDevice>> {
    return this._httpClient.get(`/api/users/${userId}/mfa/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((user) => new UserMfaDevice(user)),
      })),
    );
  }

  public createMfa(userId: string, data: any): Observable<UserMfaDevice> {
    return this._httpClient.post(`/api/users/${userId}/mfa/`, data).pipe(
      map((response: any) => new UserMfaDevice(response)),
      tap({
        error: () =>
          this._toastrService.error(t('users.toasts.credentialsConfirmFailed')),
      }),
    );
  }

  public deleteMfaWithPassword(
    userId: string,
    mfaId: string,
    password: string,
  ): Observable<Object> {
    return this._httpClient
      .delete(`/api/users/${userId}/mfa/${mfaId}/`, {
        body: { password },
      })
      .pipe(
        tap({
          next: () => this._toastrService.success(t('users.toasts.authAccountDeleted')),
          error: () =>
            this._toastrService.error(t('users.toasts.authAccountDeleteFailed')),
        }),
      );
  }

  public getBackupCodes(userId: string, data: any): Observable<MfaBackup> {
    return this._httpClient.post(`/api/users/${userId}/mfa/backup-codes/`, data).pipe(
      map((response: any) => new MfaBackup(response)),
      tap({
        error: () =>
          this._toastrService.error(t('users.toasts.backupCodesRetrieveFailed')),
      }),
    );
  }

  public genBackupCodes(userId: string, data: any): Observable<MfaBackup> {
    return this._httpClient.post(`/api/users/${userId}/mfa/backup-codes/generate/`, data).pipe(
      map((response: any) => new MfaBackup(response)),
      tap({
        error: () =>
          this._toastrService.error(t('users.toasts.backupCodesGenerateFailed')),
      }),
    );
  }

  public activateMfa(userId: string, mfaId: any, data: any): Observable<UserMfaDevice> {
    return this._httpClient.post(`/api/users/${userId}/mfa/${mfaId}/activate/`, data).pipe(
      map((response: any) => new UserMfaDevice(response)),
      tap({
        next: () =>
          this._toastrService.success(t('users.toasts.mfaActivated')),
        error: () =>
          this._toastrService.error(t('users.toasts.mfaActivateFailed')),
      }),
    );
  }

  public deactivateMfa(userId: string, mfaId: any, data: any): Observable<UserMfaDevice> {
    return this._httpClient.post(`/api/users/${userId}/mfa/${mfaId}/deactivate/`, data).pipe(
      map((response: any) => new UserMfaDevice(response)),
      tap({
        next: () =>
          this._toastrService.success(t('users.toasts.mfaDeactivated')),
        error: () =>
          this._toastrService.error(t('users.toasts.mfaDeactivateFailed')),
      }),
    );
  }

  public updateSettings(userId: string, data: any): Observable<Object> {
    return this._httpClient
      .patch(`/api/users/${userId}/settings/`, data)
      .pipe(map((response: any) => new SnLiveUser(response)));
  }

  public breadcrumbs(params?: any): Observable<any> {
    return this._httpClient.get('/api/users/breadcrumbs/', { params });
  }

  public getUsersBrief(params?: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    return this._httpClient.get('/api/users/brief/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((user) => new SnUser(user)),
      })),
    );
  }
}
